<script setup>
import { computed } from 'vue';
import { format, isValid } from 'date-fns';
import SettingsSection from 'dashboard/components/SettingsSection.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

// Read-only. Never show api_key, verification_pin or webhook_verify_token from provider_config.
const providerConfig = computed(() => props.inbox.provider_config || {});
const businessAccountId = computed(
  () => providerConfig.value.business_account_id
);
const phoneNumberId = computed(() => providerConfig.value.phone_number_id);

const phoneNumber = computed(() => {
  const number = props.inbox.phone_number;
  if (!number) return '';
  return number.startsWith('+') ? number : `+${number}`;
});

const managerUrl = computed(
  () =>
    `https://business.facebook.com/wa/manage/phone-numbers/?waba_id=${encodeURIComponent(businessAccountId.value)}`
);

const templates = computed(() =>
  Array.isArray(props.inbox.message_templates)
    ? props.inbox.message_templates
    : []
);
const approvedTemplatesCount = computed(
  () => templates.value.filter(({ status }) => status === 'APPROVED').length
);

const templatesSyncedAt = computed(() => {
  const value = props.inbox.message_templates_last_updated;
  if (!value) return '';
  const date = new Date(value);
  return isValid(date) ? format(date, 'd.M.yyyy H:mm') : '';
});
</script>

<template>
  <SettingsSection
    :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.TITLE')"
    :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.SUBTITLE')"
    :show-border="false"
  >
    <dl
      class="grid grid-cols-1 sm:grid-cols-[minmax(0,14rem)_1fr] gap-x-6 gap-y-3 text-sm m-0"
    >
      <dt class="font-medium text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.PROVIDER') }}
      </dt>
      <dd class="m-0 text-n-slate-12" data-test-id="wa-provider">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.PROVIDER_VALUE') }}
        <span class="block text-n-slate-11">
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.EMBEDDED_SIGNUP')
          }}
        </span>
      </dd>

      <dt class="font-medium text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.STATUS') }}
      </dt>
      <dd class="m-0" data-test-id="wa-status">
        <template v-if="inbox.reauthorization_required">
          <span class="font-medium text-n-ruby-11">
            {{ $t('INBOX_MGMT.LIST.RECONNECT_REQUIRED') }}
          </span>
          <span class="block text-n-slate-11">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.RECONNECT_HINT')
            }}
          </span>
        </template>
        <span v-else class="font-medium text-n-teal-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.CONNECTED') }}
        </span>
      </dd>

      <template v-if="phoneNumber">
        <dt class="font-medium text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.PHONE_NUMBER') }}
        </dt>
        <dd class="m-0 text-n-slate-12" data-test-id="wa-phone">
          {{ phoneNumber }}
        </dd>
      </template>

      <template v-if="businessAccountId">
        <dt class="font-medium text-n-slate-11">
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.BUSINESS_ACCOUNT')
          }}
        </dt>
        <dd class="m-0 text-n-slate-12" data-test-id="wa-business-account">
          <span class="font-mono">{{ businessAccountId }}</span>
          <a
            class="block text-n-blue-11 hover:underline"
            :href="managerUrl"
            target="_blank"
            rel="noopener noreferrer"
            data-test-id="wa-manager-link"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.OPEN_MANAGER')
            }}
          </a>
        </dd>
      </template>

      <template v-if="phoneNumberId">
        <dt class="font-medium text-n-slate-11">
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.PHONE_NUMBER_ID')
          }}
        </dt>
        <dd class="m-0 font-mono text-n-slate-12" data-test-id="wa-phone-id">
          {{ phoneNumberId }}
        </dd>
      </template>

      <dt class="font-medium text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.TEMPLATES') }}
      </dt>
      <dd class="m-0 text-n-slate-12" data-test-id="wa-templates">
        {{
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.TEMPLATES_COUNT', {
            approved: approvedTemplatesCount,
            total: templates.length,
          })
        }}
        <span class="block text-n-slate-11" data-test-id="wa-templates-synced">
          {{
            templatesSyncedAt
              ? $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.TEMPLATES_SYNCED',
                  { date: templatesSyncedAt }
                )
              : $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.TEMPLATES_NOT_SYNCED'
                )
          }}
        </span>
        <router-link
          class="block text-n-blue-11 hover:underline"
          :to="{ name: 'settings_templates' }"
          data-test-id="wa-templates-link"
        >
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CONNECTION.MANAGE_TEMPLATES')
          }}
        </router-link>
      </dd>
    </dl>
  </SettingsSection>
</template>
